/**
 * Mid-flow User Input nodes for the editor's Test Run (#2964).
 *
 * Test Run streams through the Explorer chat path, which has no checkpointer,
 * so a User Input node reached mid-flow cannot pause the run to ask. Instead
 * the test panel collects an answer per node up front and sends it under the
 * node's `inputId` in the same `{inputId: {text}}` envelope the backend reads
 * for a headless run (`_create_user_input_node`, graph_builder.py). Fields
 * declared on the START node are handled separately by `resolveStartFields`.
 */
export type TestRunUserInputNode = {
  nodeId: string
  inputId: string
  /** The node's prompt, used as the answer field's label. Empty when unset. */
  prompt: string
}

type NodeLike = { id: string; type?: string; data?: unknown }

/** `overrides.user_input_data` as the backend reads it (see the hook's
 *  `TestRunUserInputData`): START text fields by key, User Input answers as
 *  `{text}` by inputId. Declared here too so this module stays hook-free. */
export type TestRunUserInputPayload = Record<string, string | { text: string }>

/**
 * Build the Test Run `user_input_data` payload.
 *
 * START-node TEXT fields ride per key (`text_0: "..."`): every field of the
 * dynamic form, or the lone free-form textarea mapped onto the first text
 * field on the legacy path. File fields never appear here (they ride the
 * upload pipeline). Mid-flow User Input answers ride as `{inputId: {text}}`;
 * an empty answer is left out so the node reports plainly that nothing was
 * collected rather than receiving "" as if it were an answer (#2964).
 */
export function buildTestRunUserInputData(params: {
  useFieldForm: boolean
  textFieldKeys: readonly string[]
  fieldTextValues: Record<string, string | undefined>
  inputValue: string
  userInputNodes: readonly TestRunUserInputNode[]
  userInputAnswers: Record<string, string | undefined>
}): TestRunUserInputPayload {
  const data: TestRunUserInputPayload = {}
  if (params.useFieldForm) {
    for (const key of params.textFieldKeys) {
      const value = params.fieldTextValues[key]?.trim() ?? ""
      if (value.length > 0) data[key] = value
    }
  } else {
    const trimmed = params.inputValue.trim()
    if (trimmed.length > 0 && params.textFieldKeys.length > 0) {
      data[params.textFieldKeys[0]] = trimmed
    }
  }
  for (const node of params.userInputNodes) {
    const answer = params.userInputAnswers[node.inputId]?.trim() ?? ""
    if (answer.length > 0) data[node.inputId] = { text: answer }
  }
  return data
}

export function resolveTestRunUserInputNodes(nodes: readonly NodeLike[]): TestRunUserInputNode[] {
  const result: TestRunUserInputNode[] = []
  for (const node of nodes) {
    if (node.type !== "userInput") continue
    const data = (node.data ?? {}) as { inputId?: unknown; promptMessage?: unknown }
    const inputId =
      typeof data.inputId === "string" && data.inputId.length > 0
        ? data.inputId
        : `userInput-${node.id}`
    const prompt = typeof data.promptMessage === "string" ? data.promptMessage.trim() : ""
    result.push({ nodeId: node.id, inputId, prompt })
  }
  return result
}
